import $ from "jquery";

/**
 * Foutmeldingen laten verdwijnen zodra het veld goed is ingevuld.
 *
 * Gravity Forms 3.0 valideert uitsluitend server-side: bij verzenden komt het
 * formulier terug met de fouten al in de HTML gebakken, en daar luistert daarna
 * niets meer naar wat de bezoeker intypt. De melding bleef dus staan tot de
 * volgende verzendpoging, ook als het veld allang klopte.
 *
 * Deze module beoordeelt een veld met een foutmelding opnieuw zodra de bezoeker
 * het aanpast, en haalt de foutstaat weg als het lokaal te controleren regels
 * doorstaat. Er worden nooit fouten bíj gezet: bij verzenden blijft de server de
 * enige die bepaalt of het formulier klopt.
 *
 * Gelaagd, omdat niet elke regel lokaal na te spelen is:
 *
 * - Velden waarvan we de regel volledig kennen (verplicht, aantallen, keuzes)
 *   worden tijdens het typen beoordeeld, dus de melding verdwijnt meteen.
 * - E-mail, telefoon en datum pas bij het verlaten van het veld. Hun strengste
 *   controles leven alleen server-side (geblokkeerde adressen in
 *   `sb_block_specific_emails`, het formaat via gp-advanced-phone-field), dus
 *   een oordeel halverwege het typen zou heen en weer springen.
 *
 * De foutstaat zit in drie dingen tegelijk, en die moeten alle drie weg, anders
 * blijft een schermlezer een fout melden die niet meer op het scherm staat:
 * `gfield_error` op de wrapper, `aria-invalid` op de invoer en het
 * meldingselement waar `aria-describedby` naar wijst.
 *
 * `gform_validation_error` op de wrapper laten we bewust staan: gf-form-funnel
 * leest die klasse om een verzendpoging na een fout te herkennen.
 */

const CONSTANTS = {
    FIELD: ".gfield.gfield_error",
    MESSAGE: ".gfield_validation_message, .validation_message",
    CONTROLS: "input, select, textarea",
    // Pas beoordelen bij het verlaten van het veld, zie de toelichting hierboven.
    DEFERRED_TYPES: ["email", "phone", "date"],
    // Te complex of te duur om lokaal te beoordelen; die meldingen blijven staan
    // tot de server opnieuw oordeelt.
    SKIPPED_TYPES: ["fileupload", "post_image", "captcha", "creditcard", "list", "total"],
    EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
    DATE_FORMAT_PATTERN: /\b(mdy|dmy|ymd)(?:_(slash|dot|dash))?\b/,
    DATE_SEPARATORS: { slash: "/", dot: ".", dash: "-" },
};

/**
 * Het GF-veldtype, dat als `gfield--type-{type}` op de wrapper staat.
 */
function fieldType($field) {
    const match = /gfield--type-([a-z_]+)/.exec($field.attr("class") || "");

    return match ? match[1] : "";
}

/**
 * De invoerelementen waar de bezoeker zelf iets in kwijt kan. Verborgen en
 * uitgeschakelde velden tellen niet mee, want daar kan niemand een fout in
 * herstellen.
 */
function controls($field) {
    return $field.find(CONSTANTS.CONTROLS).not('[type="hidden"], :disabled');
}

function isRequired($field) {
    return $field.hasClass("gfield_contains_required");
}

function isFilled(control) {
    const $control = $(control);
    const type = ($control.attr("type") || "").toLowerCase();

    if (type === "checkbox" || type === "radio") {
        return $control.is(":checked");
    }

    return String($control.val() || "").trim() !== "";
}

function firstValue($field) {
    return String(controls($field).first().val() || "").trim();
}

/**
 * Standaardregel: alles wat ingevuld moet worden, is ingevuld.
 *
 * Staan er `aria-required`-invoeren in het veld, dan gelden alleen die. Zo hoeft
 * bij een naamveld het tussenvoegsel niet mee te tellen terwijl voor- en
 * achternaam dat wel doen.
 */
function requiredControlsFilled($field) {
    const $controls = controls($field);

    if ($controls.length === 0) {
        return false;
    }

    // Een optioneel veld dat leeg is, is in orde: de fout ging dan over het
    // formaat van een waarde die er nu niet meer staat.
    if (!isRequired($field) && !$controls.toArray().some(isFilled)) {
        return true;
    }

    const $marked = $controls.filter('[aria-required="true"]');
    const $relevant = $marked.length > 0 ? $marked : $controls;

    return $relevant.toArray().every(isFilled);
}

function oneChoiceChecked($field) {
    return $field.find('input[type="checkbox"]:checked, input[type="radio"]:checked').length > 0;
}

function emailIsValid($field) {
    const value = firstValue($field);

    if (value === "") {
        return !isRequired($field);
    }

    return CONSTANTS.EMAIL_PATTERN.test(value);
}

/**
 * Getal binnen de grenzen die GF als `min` en `max` op de invoer zet (bij het
 * aantallenveld de reisgezelschap-grenzen, 1 tot en met 8 volwassenen).
 */
function numberIsInRange($field) {
    const $input = controls($field).first();
    const value = String($input.val() || "").trim();

    if (value === "") {
        return !isRequired($field);
    }

    const number = Number(value.replace(",", "."));

    if (!Number.isFinite(number)) {
        return false;
    }

    const min = parseFloat($input.attr("min"));
    const max = parseFloat($input.attr("max"));

    if (Number.isFinite(min) && number < min) {
        return false;
    }

    return !(Number.isFinite(max) && number > max);
}

/**
 * Lees de datum uit het veld. Het formaat staat als klasse op de invoer
 * (`dmy_dash` bij ons), in dezelfde notatie die GF zelf gebruikt.
 */
function parseDate(value, $input) {
    const match = CONSTANTS.DATE_FORMAT_PATTERN.exec($input.attr("class") || "");
    const order = match ? match[1] : "mdy";
    const separator = CONSTANTS.DATE_SEPARATORS[match && match[2] ? match[2] : "slash"];
    const parts = value.split(separator).map((part) => parseInt(part, 10));

    if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) {
        return null;
    }

    const day = parts[order.indexOf("d")];
    const month = parts[order.indexOf("m")];
    const year = parts[order.indexOf("y")];
    const date = new Date(year, month - 1, day);

    const isRealDate =
        date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

    return isRealDate ? date : null;
}

/**
 * Valt de datum binnen de grenzen van GP Limit Dates?
 *
 * `getDateLimits()` en `isDateInRange()` zijn de methodes waarmee de plugin zelf
 * valideert. Vallen ze weg bij een plugin-update, dan houden we het op "ingevuld
 * is goed genoeg" en corrigeert de server bij verzenden.
 */
function dateIsWithinLimits($input, date) {
    const limitDates = window.GPLimitDates;

    if (
        !limitDates ||
        typeof limitDates.getDateLimits !== "function" ||
        typeof limitDates.isDateInRange !== "function"
    ) {
        return true;
    }

    try {
        const limits = limitDates.getDateLimits($input);

        return limitDates.isDateInRange(date, limits.min, limits.max);
    } catch (error) {
        return true;
    }
}

function dateIsValid($field) {
    const $input = controls($field).first();
    const value = String($input.val() || "").trim();

    if (value === "") {
        return !isRequired($field);
    }

    const date = parseDate(value, $input);

    if (!date) {
        return false;
    }

    return dateIsWithinLimits($input, date);
}

/**
 * De regel per veldtype. Wat hier niet in staat valt terug op
 * `requiredControlsFilled`, en dat is precies wat we voor telefoon willen: het
 * formaat controleert gp-advanced-phone-field server-side, en dat oordeel
 * kunnen we hier niet overdoen zonder het risico dat we een nummer goedkeuren
 * dat straks alsnog wordt afgewezen.
 */
const RULES = {
    checkbox: oneChoiceChecked,
    radio: oneChoiceChecked,
    consent: oneChoiceChecked,
    multiselect: requiredControlsFilled,
    email: emailIsValid,
    number: numberIsInRange,
    quantity: numberIsInRange,
    date: dateIsValid,
};

/**
 * Haal de foutstaat weg: de klasse van de wrapper, `aria-invalid` van de
 * invoeren, het meldingselement en de verwijzing daarnaartoe in
 * `aria-describedby`. Andere ids in dat attribuut (de gewone veldomschrijving)
 * blijven staan.
 */
function clearFieldError($field) {
    const $message = $field.find(CONSTANTS.MESSAGE);
    const messageIds = $message
        .map(function () {
            return this.id;
        })
        .get()
        .filter(Boolean);

    $field.removeClass("gfield_error");
    $field.find('[aria-invalid="true"]').attr("aria-invalid", "false");

    $field.find("[aria-describedby]").each(function () {
        const remaining = String($(this).attr("aria-describedby") || "")
            .split(/\s+/)
            .filter((id) => id !== "" && !messageIds.includes(id));

        if (remaining.length > 0) {
            $(this).attr("aria-describedby", remaining.join(" "));
        } else {
            $(this).removeAttr("aria-describedby");
        }
    });

    $message.remove();
}

/**
 * Beoordeel een veld met een foutmelding opnieuw en wis de fout als het klopt.
 */
function refresh($field) {
    if ($field.length === 0 || !$field.hasClass("gfield_error")) {
        return;
    }

    const type = fieldType($field);

    if (CONSTANTS.SKIPPED_TYPES.includes(type)) {
        return;
    }

    const isValid = RULES[type] || requiredControlsFilled;

    if (isValid($field)) {
        clearFieldError($field);
    }
}

/**
 * Event handler - tijdens het typen, voor de velden die we volledig kunnen
 * beoordelen.
 */
$(document).on("input", `${CONSTANTS.FIELD} input, ${CONSTANTS.FIELD} textarea`, function () {
    const $field = $(this).closest(CONSTANTS.FIELD);

    if (CONSTANTS.DEFERRED_TYPES.includes(fieldType($field))) {
        return;
    }

    refresh($field);
});

/**
 * Event handler - keuze gemaakt of veld verlaten. Vangt de select2-selects, de
 * plus/min-knoppen bij de aantallen en de datumprikker, en is het moment waarop
 * e-mail, telefoon en datum aan de beurt zijn.
 */
$(document).on(
    "change focusout",
    `${CONSTANTS.FIELD} input, ${CONSTANTS.FIELD} select, ${CONSTANTS.FIELD} textarea`,
    function () {
        refresh($(this).closest(CONSTANTS.FIELD));
    }
);
